<!-- apps/ers/src/app/modules/ers-leave-management/dialogs/hourly-permission-dialog/hourly-permission-dialog.component.html -->
<!-- PBI 19505 – Hourly Permission -->

<div class="dialog-header">
  <h2 mat-dialog-title>
    {{
      'leave_management.hourly_permission_dialog.hourly_permission' | transloco
    }}
  </h2>
  <button
    class="lm-dialog-close-circle"
    (click)="onCancel()"
    [matTooltip]="
      'leave_management.hourly_permission_dialog.matTooltip.close' | transloco
    "
  >
    <mat-icon>{{
      'leave_management.hourly_permission_dialog.close' | transloco
    }}</mat-icon>
  </button>
</div>

<mat-dialog-content>
  <form [formGroup]="hourlyForm" class="hourly-form" novalidate>
    <!-- Submit Request To + Select Date ──────────────────────────────────── -->
    <div class="row two-col">
      <div class="lm-form-group">
        <label class="lm-field-label"
          >Submit Request To <span class="lm-req">*</span></label
        >

        <!-- Single supervisor: read-only display -->
        @if (isSingleSupervisor && selectedSupervisors.length > 0) {
        <div class="hp-single-sup">
          <mat-icon class="hp-sup-icon">{{
            'leave_management.hourly_permission_dialog.person' | transloco
          }}</mat-icon>
          <span>{{ selectedSupervisors[0].supervisorName }}</span>
        </div>
        }

        <!-- Multi-supervisor dropdown -->
        @if (!isSingleSupervisor) {
        <mat-form-field appearance="outline" class="lm-full-width">
          <mat-select
            placeholder="Select supervisor(s)"
            [disabled]="isLoadingSupervisors"
            (selectionChange)="addSupervisor($event.value)"
          >
            @if (isLoadingSupervisors) {
            <mat-option disabled>
              <mat-spinner
                diameter="18"
                style="display: inline-block; margin-right: 8px"
              ></mat-spinner>
              {{
                'leave_management.hourly_permission_dialog.loading' | transloco
              }}
            </mat-option>
            } @for (s of supervisors | ersSortBy: 'supervisorName'; track s.id)
            {
            <mat-option [value]="s">
              {{ s.supervisorName }}
            </mat-option>
            }
          </mat-select>
        </mat-form-field>
        }

        <!-- Chips -->
        @if (!isSingleSupervisor && selectedSupervisors.length > 0) {
        <div class="lm-chip-row">
          @for (s of selectedSupervisors; track s.id) {
          <span class="lm-chip">
            {{ s.supervisorName }}
            <button
              class="lm-chip-x"
              type="button"
              (click)="removeSupervisor(s)"
            >
              ×
            </button>
          </span>
          }
        </div>
        } @if (selectedSupervisors.length === 0 && !isLoadingSupervisors) {
        <p class="hp-req-hint">
          {{
            'leave_management.hourly_permission_dialog.please_select_at_least_one_supervisor'
              | transloco
          }}
        </p>
        }
      </div>

      <div class="lm-form-group">
        <label class="lm-field-label"
          >Select Date <span class="lm-req">*</span></label
        >
        <mat-form-field appearance="outline" class="lm-full-width">
          <input
            matInput
            [matDatepicker]="datePicker"
            formControlName="requestDate"
            placeholder="MM/DD/YYYY"
            [min]="today"
            [matDatepickerFilter]="futureDateFilter"
            (click)="datePicker.open()"
          />
          <mat-datepicker-toggle matSuffix [for]="datePicker">
            <mat-icon matDatepickerToggleIcon>{{
              'leave_management.hourly_permission_dialog.calendar_today'
                | transloco
            }}</mat-icon>
          </mat-datepicker-toggle>
          <mat-datepicker #datePicker></mat-datepicker>
          @if (hourlyForm.get('requestDate')?.hasError('required')) {
          <mat-error>
            {{
              'leave_management.hourly_permission_dialog.date_is_required'
                | transloco
            }}
          </mat-error>
          }
        </mat-form-field>
      </div>
    </div>

    <!-- Leave Type dropdown ────────────────────────────────────────────── -->
    <div class="row one-col">
      <div class="lm-form-group">
        <label class="lm-field-label"
          >Leave Type <span class="lm-req">*</span></label
        >
        <mat-form-field appearance="outline" class="lm-full-width">
          <input
            matInput
            #leaveTypeInput
            [formControl]="leaveTypeSearchCtrl"
            [matAutocomplete]="leaveTypeAuto"
            [disabled]="isLoadingLeaveTypes"
            placeholder="{{
              isLoadingLeaveTypes ? 'Loading…' : 'Search or select leave type…'
            }}"
            autocomplete="off"
          />
          <button
            mat-icon-button
            matSuffix
            type="button"
            (click)="toggleLeaveTypePanel($event)"
            tabindex="-1"
            [disabled]="isLoadingLeaveTypes"
          >
            <mat-icon>{{
              'leave_management.hourly_permission_dialog.arrow_drop_down'
                | transloco
            }}</mat-icon>
          </button>
          <mat-autocomplete
            #leaveTypeAuto="matAutocomplete"
            (optionSelected)="onLeaveTypeSelected($event)"
          >
            @for (t of filteredLeaveTypes; track t.id) {
            <mat-option [value]="t.id">
              {{ t.name }}
            </mat-option>
            } @if (filteredLeaveTypes.length === 0 && !isLoadingLeaveTypes) {
            <mat-option disabled>
              {{
                'leave_management.hourly_permission_dialog.no_results'
                  | transloco
              }}
            </mat-option>
            }
          </mat-autocomplete>
          @if (hourlyForm.get('leaveTypeId')?.hasError('required')) {
          <mat-error>
            {{
              'leave_management.hourly_permission_dialog.leave_type_is_required'
                | transloco
            }}
          </mat-error>
          }
        </mat-form-field>
      </div>
    </div>

    <!-- Start Time / End Time ──────────────────────────────────────────── -->
    <div class="row two-col">
      <div class="lm-form-group">
        <label class="lm-field-label"
          >Start Time <span class="lm-req">*</span></label
        >
        <div class="native-input-wrapper">
          <input
            type="time"
            formControlName="fromTime"
            class="native-time-input"
          />
        </div>
        @if (hourlyForm.get('fromTime')?.value) {
        <span class="hp-time-hint">
          {{ format12h(hourlyForm.get('fromTime')?.value) }}
        </span>
        } @if (hourlyForm.get('fromTime')?.touched &&
        hourlyForm.get('fromTime')?.hasError('required')) {
        <mat-error class="hp-field-error">
          {{
            'leave_management.hourly_permission_dialog.start_time_is_required'
              | transloco
          }}
        </mat-error>
        }
      </div>

      <div class="lm-form-group">
        <label class="lm-field-label"
          >End Time <span class="lm-req">*</span></label
        >
        <div class="native-input-wrapper">
          <input
            type="time"
            formControlName="toTime"
            class="native-time-input"
          />
        </div>
        @if (hourlyForm.get('toTime')?.value) {
        <span class="hp-time-hint">
          {{ format12h(hourlyForm.get('toTime')?.value) }}
        </span>
        } @if (hourlyForm.get('toTime')?.touched &&
        hourlyForm.get('toTime')?.hasError('required')) {
        <mat-error class="hp-field-error">
          {{
            'leave_management.hourly_permission_dialog.end_time_is_required'
              | transloco
          }}
        </mat-error>
        } @if (hourlyForm.hasError('endTimeNotAfterStart')) {
        <mat-error class="hp-field-error">
          {{
            'leave_management.hourly_permission_dialog.end_time_must_be_after_start_time'
              | transloco
          }}
        </mat-error>
        }
      </div>
    </div>

    <!-- Reason dropdown ────────────────────────────────────────────────── -->
    <div class="row one-col">
      <div class="lm-form-group">
        <label class="lm-field-label"
          >Reason <span class="lm-req">*</span></label
        >
        <lib-ers-searchable-select
          formControlName="reasonCode"
          [options]="reasons"
          optionValue="value"
          optionLabel="label"
          [placeholder]="
            'leave_management.hourly_permission_dialog.placeholder.select_reason'
              | transloco
          "
        >
          @if (hourlyForm.get('reasonCode')?.hasError('required')) {
          <mat-error>
            {{
              'leave_management.hourly_permission_dialog.reason_is_required'
                | transloco
            }}
          </mat-error>
          }
        </lib-ers-searchable-select>
      </div>
    </div>

    <!-- Please Specify (shown only when "Other" is selected) ───────────── -->
    @if (showOtherReason) {
    <div class="row one-col">
      <div class="lm-form-group">
        <label class="lm-field-label"
          >Please Specify <span class="lm-req">*</span></label
        >
        <mat-form-field appearance="outline" class="lm-full-width">
          <textarea
            matInput
            formControlName="pleaseSpecify"
            rows="3"
            maxlength="500"
            [placeholder]="
              'leave_management.hourly_permission_dialog.placeholder.describe_your_reason'
                | transloco
            "
          ></textarea>
          <mat-hint align="end">
            {{ hourlyForm.get('pleaseSpecify')?.value?.length ?? 0 }} / 500
          </mat-hint>
          @if (hourlyForm.get('pleaseSpecify')?.hasError('required')) {
          <mat-error>
            {{
              'leave_management.hourly_permission_dialog.please_specify_the_reason'
                | transloco
            }}
          </mat-error>
          }
        </mat-form-field>
      </div>
    </div>
    }
  </form>
</mat-dialog-content>

<mat-dialog-actions align="end" class="actions-row">
  <button
    type="button"
    class="lm-btn lm-btn--reset"
    (click)="onCancel()"
    [disabled]="isSubmitting"
  >
    {{ 'leave_management.hourly_permission_dialog.cancel' | transloco }}
  </button>
  <button
    type="button"
    class="lm-btn lm-btn--primary"
    (click)="onSubmit()"
    [disabled]="
      hourlyForm.invalid || selectedSupervisors.length === 0 || isSubmitting
    "
  >
    @if (isSubmitting) {
    <mat-spinner
      diameter="18"
      style="display: inline-block; margin-right: 6px; vertical-align: middle"
    ></mat-spinner>
    }
    {{ isSubmitting ? 'Submitting…' : 'Submit Request' }}
  </button>
</mat-dialog-actions>
